import { sheetRows } from "./sheet";

/*
 * The paper face as a PDF, for the one place the browser cannot print it.
 *
 * An iPhone running the app from the Home Screen ignores window.print()
 * (iOS 27.0.1, 2026-10-11): no dialog, no error. Its share sheet does offer
 * Print for a PDF file, so there the Print button hands one over instead.
 *
 * This is a second drawing of the sheet `Paper/index.css` lays out, and the
 * numbers below are that stylesheet's, converted to millimetres. Change one
 * and change the other: DESIGN.md's Paper Face section is the authority.
 */

const PT = 25.4 / 72; // mm per point
const PX = 25.4 / 96; // mm per CSS pixel

const PAGE = { width: 210, height: 297, margin: 14 }; // A4, `@page { margin: 14mm }`
const LINE = 1.35; // `.Paper { line-height: 1.35 }`
const PAD_X = 2; // cell padding, 2.4mm 2mm
const PAD_Y = 2.4;

// `.Paper-date` is dealt one of three widths, padding included.
const DATE_WIDTH = { none: 23, ddd: 32, dddd: 42 };
const DATE_PAD_RIGHT = 3;

const TITLE = { size: 16, weight: 600, gap: 5 };
const HEAD = { size: 9, weight: 600, tracking: 0.1 }; // tracking in em
const DATE = { size: 9.5, weight: 500 };

const RULE = { outer: 1.5 * PX, head: 1 * PX, row: 0.5 * PX };
const BOX = { size: 4.2, border: 0.6 * PX, radius: 0.6, check: 12 * PX, stroke: 3 * (12 / 24) * PX };

const HEAD_HEIGHT = 2 * PAD_Y + HEAD.size * LINE * PT + RULE.head;
// A body row as the browser lays it out: 38.02px, measured (DESIGN.md), not
// derived — the padding and the 10.5pt strut alone come to about 37px.
const ROW_HEIGHT = 38.02 * PX;

// The paper face's own chain, for text Barlow has no glyphs for. See the
// comment at the top of Paper/index.css for why it is not `--font-ui`.
const FALLBACK_FAMILY = `"Barlow", Arial, Helvetica, "PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif`;

const ELLIPSIS = "…";

/** Register the three Barlow weights the sheet uses with a fresh document. */
function newDocument(kit) {
  const doc = new kit.jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
  for (const weight of [400, 500, 600]) {
    const file = `Barlow-${weight}.ttf`;
    doc.addFileToVFS(file, kit.fonts[weight]);
    doc.addFont(file, "Barlow", "normal", weight);
  }
  return doc;
}

function setStyle(doc, { size, weight }) {
  doc.setFont("Barlow", "normal", weight);
  doc.setFontSize(size);
}

/** True when the current font has a glyph for every character of `text`. */
function covered(doc, text) {
  const map = doc.getFont().metadata?.cmap?.unicode?.codeMap;
  if (!map) return true;
  for (const ch of text) {
    if (map[ch.codePointAt(0)] === undefined) return false;
  }
  return true;
}

/** Width of `text` in the current font, tracking included. */
function widthOf(doc, text, charSpace) {
  return doc.getTextWidth(text) + charSpace * Array.from(text).length;
}

/** `text` cut with an ellipsis to fit `max`, as `text-overflow: ellipsis` does. */
function fit(measure, text, max) {
  if (measure(text) <= max) return text;
  const chars = Array.from(text);
  let lo = 0;
  let hi = chars.length;
  // The longest prefix that still fits once the ellipsis is added.
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (measure(chars.slice(0, mid).join("") + ELLIPSIS) <= max) lo = mid;
    else hi = mid - 1;
  }
  return lo > 0 ? chars.slice(0, lo).join("") + ELLIPSIS : "";
}

/**
 * Draw one line of text, left-aligned at `x` and centred on `midY`, cut to
 * `max`. Barlow covers Latin, so most text goes in as real text. A name it
 * cannot set — Chinese, an emoji — is drawn by the browser in the paper
 * face's fallback chain and placed as an image, rather than printed as
 * missing glyphs.
 */
function drawText(doc, raster, text, x, midY, max, style) {
  setStyle(doc, style);
  const image = raster && !covered(doc, text) ? raster(text, style, max) : null;
  if (image) {
    doc.addImage(image.dataUrl, "PNG", x, midY - image.height / 2, image.width, image.height, image.alias);
    return;
  }
  // Barlow covers it, or nothing could draw it any better.
  const charSpace = (style.tracking || 0) * style.size * PT;
  const shown = fit((t) => widthOf(doc, t, charSpace), text, max);
  if (shown) doc.text(shown, x, midY, { baseline: "middle", charSpace });
}

function rule(doc, y, weight) {
  doc.setLineWidth(weight);
  doc.line(PAGE.margin, y, PAGE.width - PAGE.margin, y);
}

function drawBox(doc, x, midY, done) {
  const top = midY - BOX.size / 2;
  doc.setLineWidth(BOX.border);
  if (!done) {
    doc.roundedRect(x, top, BOX.size, BOX.size, BOX.radius, BOX.radius, "S");
    return;
  }
  doc.roundedRect(x, top, BOX.size, BOX.size, BOX.radius, BOX.radius, "FD");
  // The check is the same 24-unit path the printed sheet draws, at 12px.
  const s = BOX.check / 24;
  const ox = x + (BOX.size - BOX.check) / 2;
  const oy = top + (BOX.size - BOX.check) / 2;
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(BOX.stroke);
  doc.setLineCap("round");
  doc.setLineJoin("round");
  doc.lines(
    [
      [4.5 * s, 4.5 * s],
      [9.5 * s, -9.5 * s],
    ],
    ox + 5 * s,
    oy + 12.5 * s,
    [1, 1],
    "S",
    false
  );
  doc.setDrawColor(0, 0, 0);
  doc.setLineCap("butt");
  doc.setLineJoin("miter");
}

/**
 * Build the sheet as a jsPDF document. `kit` is what `loadPdfKit` resolves to;
 * `raster` draws text Barlow cannot (see `canvasRaster`) and may be omitted.
 * Synchronous on purpose: iOS opens the share sheet only from inside the tap,
 * so the file is built there, with nothing awaited.
 */
export function buildSheetPdf(kit, props, raster = null) {
  const { slots, title, shouldPrintHeader, showWeekdays, weekdayFormat } = props;
  const rows = sheetRows(props);
  const doc = newDocument(kit);
  doc.setProperties({ title: title || "Schedule" });
  doc.setDrawColor(0, 0, 0);
  doc.setFillColor(0, 0, 0);
  doc.setTextColor(0, 0, 0);

  const left = PAGE.margin;
  const right = PAGE.width - PAGE.margin;
  const bottom = PAGE.height - PAGE.margin;
  const dateWidth = DATE_WIDTH[showWeekdays ? weekdayFormat : "none"] || DATE_WIDTH.none;
  const colWidth = (right - left - dateWidth) / Math.max(1, slots.length);
  const colX = (i) => left + dateWidth + i * colWidth;

  let y = PAGE.margin;

  if (shouldPrintHeader && title) {
    const height = TITLE.size * LINE * PT;
    drawText(doc, raster, title, left, y + height / 2, right - left, TITLE);
    y += height + TITLE.gap;
  }

  const header = () => {
    rule(doc, y, RULE.outer);
    const mid = y + HEAD_HEIGHT / 2;
    drawText(doc, raster, "DATE", left + PAD_X, mid, dateWidth - PAD_X - DATE_PAD_RIGHT, HEAD);
    slots.forEach((slot, i) => {
      // Cut at the cell's edge, not its padding: a table cell clips at its
      // padding box, so a narrow column still prints "M…" rather than nothing.
      drawText(doc, raster, String(slot.name).toUpperCase(), colX(i) + PAD_X, mid, colWidth - PAD_X, HEAD);
    });
    y += HEAD_HEIGHT;
    rule(doc, y, RULE.head);
  };

  header();
  rows.forEach((row, index) => {
    // A row is never torn across a page; the header repeats on the next one.
    if (y + ROW_HEIGHT > bottom) {
      doc.addPage();
      y = PAGE.margin;
      header();
    }
    const mid = y + ROW_HEIGHT / 2;
    drawText(doc, raster, row.label, left + PAD_X, mid, dateWidth - PAD_X - DATE_PAD_RIGHT, DATE);
    row.done.forEach((done, i) => drawBox(doc, colX(i) + PAD_X, mid, done));
    y += ROW_HEIGHT;
    rule(doc, y, index === rows.length - 1 ? RULE.outer : RULE.row);
  });
  if (rows.length === 0) rule(doc, y, RULE.outer);

  return doc;
}

/** The sheet as a File, named after the title, ready for navigator.share. */
export function sheetPdfFile(kit, props, raster = null) {
  const doc = buildSheetPdf(kit, props, raster);
  const name =
    (String(props.title || "")
      .replace(/[\\/:*?"<>|]+/g, " ")
      .trim() || "Schedule") + ".pdf";
  return new File([doc.output("blob")], name, { type: "application/pdf" });
}

/**
 * Text Barlow cannot set, drawn on a canvas by the browser and returned as a
 * PNG sized in millimetres. Cut to `max` by the same ellipsis rule; tracking
 * is dropped, as DESIGN.md asks for CJK labels.
 */
export function canvasRaster(doc = document) {
  const SCALE = 12; // canvas pixels per millimetre, about 300dpi
  let canvas = null;
  let ctx = null;
  return (text, style, max) => {
    // Made on first use: most sheets are all Latin and never need it.
    if (!canvas) {
      canvas = doc.createElement("canvas");
      ctx = canvas.getContext && canvas.getContext("2d");
    }
    if (!ctx) return null;
    const font = `${style.weight} ${style.size * PT * SCALE}px ${FALLBACK_FAMILY}`;
    ctx.font = font;
    const measure = (t) => ctx.measureText(t).width / SCALE;
    const shown = fit(measure, text, max);
    if (!shown) return null;
    const width = measure(shown);
    const height = style.size * LINE * PT;
    canvas.width = Math.ceil(width * SCALE);
    canvas.height = Math.ceil(height * SCALE);
    ctx.font = font; // resizing a canvas resets its state
    ctx.fillStyle = "#000";
    ctx.textBaseline = "middle";
    ctx.fillText(shown, 0, canvas.height / 2);
    return { dataUrl: canvas.toDataURL("image/png"), width, height, alias: `${font}|${shown}` };
  };
}
